import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tasksApi } from '@/api/endpoints/tasks'
import type { Task, TaskStats } from '@/types'
import type { TaskQueryParams, CreateTaskData, UpdateTaskData } from '@/api/endpoints/tasks'

// ===== Paginated tasks list =====

export function useTasks(params: TaskQueryParams) {
  const query = useQuery({
    queryKey: ['tasks', params],
    queryFn: async () => {
      try {
        const res = await tasksApi.getTasks(params)
        return res.data
      } catch (err) {
        console.error('[useTasks] Failed to fetch tasks:', err)
        return {
          data: [] as Task[],
          total: 0,
          page: 1,
          pageSize: params.size ?? 20,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        }
      }
    },
    staleTime: 30_000,
  })

  // Handle both flat list (meta present) and paginated object
  const raw = query.data
  const taskList  = Array.isArray(raw) ? raw : ((raw as any)?.data ?? [])
  const taskTotal = Array.isArray(raw) ? raw.length : ((raw as any)?.total ?? 0)
  const taskPages = Array.isArray(raw) ? 1 : ((raw as any)?.totalPages ?? 0)
  const taskHasNext = Array.isArray(raw) ? false : ((raw as any)?.hasNext ?? false)

  return {
    tasks: taskList,
    total: taskTotal,
    totalPages: taskPages,
    hasNext: taskHasNext,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

// ===== My tasks (current user) =====

export function useMyTasks(params?: { page?: number; size?: number; status?: string; workspaceId?: string }) {
  const query = useQuery({
    queryKey: ['tasks', 'my', params],
    queryFn: async () => {
      try {
        const res = await tasksApi.getMyTasks(params)
        return res.data
      } catch {
        return {
          data: [] as Task[],
          total: 0,
          page: 1,
          pageSize: params?.size ?? 20,
          totalPages: 0,
          hasNext: false,
          hasPrev: false,
        }
      }
    },
    staleTime: 10_000,        // 10s — refresh quickly so new assignments appear fast
    refetchOnWindowFocus: true, // re-fetch when user switches back to the tab
  })

  // Handle both flat list (meta present) and paginated object
  const raw = query.data
  const tasks = Array.isArray(raw) ? raw : ((raw as any)?.data ?? [])
  const total = Array.isArray(raw) ? raw.length : ((raw as any)?.total ?? 0)

  return {
    tasks,
    total,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

// ===== Single task =====

export function useTask(id: string, options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: ['tasks', id],
    queryFn: async () => {
      const res = await tasksApi.getTask(id)
      return res.data
    },
    enabled: options?.enabled !== false && Boolean(id),
    staleTime: 30_000,
  })

  return {
    task: query.data as Task | undefined,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  }
}

// ===== Create task =====

export function useCreateTask() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data: CreateTaskData) => tasksApi.createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['tasks', 'stats'] })
    },
  })

  return {
    createTask: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

// ===== Update task =====

export function useUpdateTask(id: string) {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data: UpdateTaskData) => tasksApi.updateTask(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', id] })
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  return {
    updateTask: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

// ===== Complete task =====

export function useCompleteTask() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (id: string) => tasksApi.completeTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  return {
    completeTask: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

// ===== Move task (drag-and-drop status change by id) =====

export function useMoveTask() {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      tasksApi.updateTask(id, { status } as UpdateTaskData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })
  return { moveTask: mutation.mutateAsync }
}

// ===== Delete task =====

export function useDeleteTask() {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (id: string) => tasksApi.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  return {
    deleteTask: mutation.mutateAsync,
    isLoading: mutation.isPending,
    error: mutation.error,
  }
}

// ===== Task stats =====

export function useTaskStats(params?: { workspaceId?: string }) {
  const query = useQuery({
    // workspaceId is part of the key: without it the first workspace's totals
    // would be replayed for every other one.
    queryKey: ['tasks', 'stats', params?.workspaceId ?? 'all'],
    queryFn: async () => {
      try {
        const res = await tasksApi.getTaskStats(params)
        return res.data
      } catch {
        return null
      }
    },
    staleTime: 30_000,
  })

  return {
    stats: query.data as TaskStats | null,
    isLoading: query.isLoading,
    error: query.error,
  }
}
